import { Select } from 'antd';
import FormField from 'components/FormField';
import { useFormikContext } from 'formik';
import { get, toNumber } from 'lodash-es';
import { useMemo, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { validStars } from 'services/api/oms';
import {
  positiveReviewStars,
  negativeReviewStars,
  isPositiveReviewScenarioType,
  isNegativeReviewScenarioType,
  scenarioTypeI18nLabelMap,
} from '../config';
import { FV, InputWrapper, Validate } from './share';
import { starConflict } from 'views/Aide/service';
import { DisabledProps } from '../TaskForm/share';

export const validate: Validate = () => {};

export interface StarsFieldProps {
  taskId?: number;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  scenarioType: Marketing.ReviewManagementScenarioType;
}

function StarsField(props: StarsFieldProps & DisabledProps) {
  const { taskId, platform, region, scenarioType, disabled } = props;

  const { t } = useTranslate();

  const selectableStars = useMemo(() => {
    if (isPositiveReviewScenarioType(scenarioType)) {
      return positiveReviewStars;
    }

    if (isNegativeReviewScenarioType(scenarioType)) {
      return negativeReviewStars;
    }

    return [];
  }, [scenarioType]);

  const options = selectableStars.map((star) => {
    return {
      value: star,
      label: t('review.x星', { count: toNumber(star) }),
    };
  });

  const {
    values: { autoReviewConfig: { stars: starsValue = [] } = {} },
    errors,
  } = useFormikContext<FV>();

  const validatedRef = useRef(false);

  const validate = (stars: Required<FV>['autoReviewConfig']['stars']) => {
    if (!stars || stars.length === 0) {
      return t('reviewSetting.请选择适用评分');
    }

    // 未修改此字段时，阻止校验接口调用
    if (starsValue === stars && validatedRef.current) {
      return get(errors, 'autoReviewConfig.stars') as string;
    }

    return (
      taskId
        ? validStars({
            taskId,
            stars,
          })
        : starConflict({
            scenarioType,
            platform,
            region,
            stars,
          })
    )
      .then((res) => {
        validatedRef.current = true;

        if (!res || res.length === 0) {
          return undefined;
        }

        const [
          {
            star,
            conflictScenarios: [firstConflictScenario],
          },
        ] = res;

        const scenarioLabel = t(scenarioTypeI18nLabelMap[firstConflictScenario]);

        return t('aide.与scenariox星冲突，请移除后再进行保存', {
          scenario: scenarioLabel,
          x: star,
        });
      })
      .catch(() => {});
  };

  return (
    <FormField<Required<FV>['autoReviewConfig']['stars']>
      required
      name="autoReviewConfig.stars"
      label={t('reviewSetting.适用评分')}
      validate={validate}
    >
      {({ field }) => {
        return (
          <InputWrapper>
            <Select
              disabled={disabled}
              mode="multiple"
              placeholder={t('reviewSetting.请选择适用评分')}
              allowClear
              options={options}
              {...field}
            />
          </InputWrapper>
        );
      }}
    </FormField>
  );
}

export default StarsField;
